import * as THREE from 'three';
import { VEHICLES, stockColorOf } from '@/entities/vehicles';
import { ModelLoader, modelLoader } from '@/render/ModelLoader';
import { applyCarMaterials } from '@/entities/CarView';
import { attachModRig } from '@/entities/mods/ModRig';
import { surfaceFromObject } from '@/entities/mods/modSurface';
import { resolveLook } from '@/entities/carLook';
import { parseLoadoutString } from '@/config/cosmetics';
import { EnvironmentBuilder } from '@/render/Environment';
import { RACER_POOL } from '@/config/racers';

/**
 * LobbyStage.ts — the online lobby SHOWROOM (M63, rebuilt M64 as a FULL-BLEED
 * scene). A transparent canvas covers the whole lobby: the photoreal garage
 * backdrop (public/lobby/bg.webp, CSS underneath) shows through, and each grid
 * seat's vehicle rotates over a glowing ring + soft contact shadow.
 *
 * M64 placement is SCREEN-DRIVEN: the lobby screen hands each seat a target
 * point in viewport fractions (so cars line up under their header columns at
 * ANY aspect — landscape, desktop, portrait), and the stage unprojects that
 * point onto the ground plane. Lower on screen = nearer the camera = bigger,
 * which is exactly how the local player's hero car gets its presence.
 */

const ROT_SPEED = 0.55; // rad/s of pedestal spin

export interface SeatTarget {
  /** Viewport fractions (0..1) where this seat's ground point should project. */
  xFrac: number;
  yFrac: number;
}

interface Seat {
  group: THREE.Group;
  carHolder: THREE.Group;
  ring: THREE.Mesh;
  ringMat: THREE.MeshBasicMaterial;
  shadowMat: THREE.MeshBasicMaterial;
  vehicleId: string;
  /** M81: the seat's equipped-cosmetics wire string (drives car re-dressing). */
  lookKey: string;
  filled: boolean;
  /** Guards async GLB loads — only the latest requested ride may land. */
  loadToken: number;
  phase: number;
}

/** Soft radial contact shadow so cars sit ON the photo floor. */
function shadowTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 6, 64, 64, 62);
  grad.addColorStop(0, 'rgba(0,0,0,0.62)');
  grad.addColorStop(0.7, 'rgba(0,0,0,0.30)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

export class LobbyStage {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(36, 16 / 9, 0.1, 300);
  private env: EnvironmentBuilder | null = null;
  private shadowTex: THREE.Texture | null = null;
  private seats: Seat[] = [];
  private localSlot = -1;
  private raf: number | null = null;
  private lastT = 0;
  private readonly reducedMotion =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(private readonly host: HTMLElement) {}

  private ensureRenderer(): void {
    if (this.renderer) return;
    const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    r.setClearColor(0x000000, 0);
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.domElement.className = 'racelobby-canvas';
    this.host.appendChild(r.domElement);
    this.renderer = r;

    // Showroom IBL so clearcoat paint + chrome reflect something (M35 lesson).
    this.env = new EnvironmentBuilder(r);
    this.scene.environment = this.env.generate({
      skyTop: 0x1a2440,
      skyHorizon: 0x3a4a6e,
      sun: 0xfff2dc,
      sunIntensity: 2.4,
      sunDir: new THREE.Vector3(0.35, 0.85, 0.45).normalize(),
      ground: 0x07090f,
    });

    this.scene.add(new THREE.HemisphereLight(0xbfd0ee, 0x11141d, 0.8));
    const key = new THREE.DirectionalLight(0xfff2dc, 2.3);
    key.position.set(6, 12, 9);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x88aaff, 1.1);
    rim.position.set(-8, 6, -10);
    this.scene.add(rim);
    // A red kick from frame-left + a blue one from the right — ties the cars
    // into the backdrop's neon.
    const redKick = new THREE.DirectionalLight(0xff2a22, 0.7);
    redKick.position.set(-14, 3, 4);
    this.scene.add(redKick);
    const blueKick = new THREE.DirectionalLight(0x3a6aff, 0.6);
    blueKick.position.set(14, 4, -2);
    this.scene.add(blueKick);

    // Fixed hero camera — placement solves seats against it.
    this.camera.position.set(0, 4.1, 20);
    this.camera.lookAt(0, 1.3, 0);

    this.shadowTex = shadowTexture();
  }

  /** (Re)build `n` seats (rings + shadows; cars arrive via setSeat). */
  setField(n: number): void {
    this.ensureRenderer();
    for (const s of this.seats) this.scene.remove(s.group);
    this.seats = [];
    for (let i = 0; i < n; i++) {
      const group = new THREE.Group();

      const shadowMat = new THREE.MeshBasicMaterial({
        map: this.shadowTex,
        transparent: true,
        depthWrite: false,
      });
      const shadow = new THREE.Mesh(new THREE.PlaneGeometry(7.4, 7.4), shadowMat);
      shadow.rotation.x = -Math.PI / 2;
      shadow.position.y = 0.015;
      group.add(shadow);

      const ringMat = new THREE.MeshBasicMaterial({
        color: RACER_POOL[i]?.color ?? 0x33415e,
        transparent: true,
        opacity: 0.2,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(new THREE.RingGeometry(2.6, 2.95, 56), ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.03;
      group.add(ring);

      const carHolder = new THREE.Group();
      group.add(carHolder);

      this.scene.add(group);
      this.seats.push({
        group,
        carHolder,
        ring,
        ringMat,
        shadowMat,
        vehicleId: '',
        lookKey: '',
        filled: false,
        loadToken: 0,
        phase: i * 0.9,
      });
    }
  }

  setLocalSlot(slot: number): void {
    this.localSlot = slot;
    this.refreshRings();
  }

  /** M64: place each seat so its ground point projects to the given viewport
   *  fraction — the lobby screen derives these from its header columns, so the
   *  cars always line up under their name plates at any window shape. */
  applyTargets(targets: (SeatTarget | null)[]): void {
    if (!this.renderer) return;
    this.camera.updateMatrixWorld();
    targets.forEach((t, slot) => {
      const seat = this.seats[slot];
      if (!seat || !t) return;
      const ndc = new THREE.Vector3(t.xFrac * 2 - 1, -(t.yFrac * 2 - 1), 0.5).unproject(
        this.camera,
      );
      const dir = ndc.sub(this.camera.position).normalize();
      if (dir.y >= -0.02) return; // above the horizon — can't land on the floor
      const k = -this.camera.position.y / dir.y;
      const p = this.camera.position.clone().addScaledVector(dir, k);
      seat.group.position.set(p.x, 0, p.z);
    });
  }

  /** Show `vehicleId` on a seat ('' clears it). M81: `look` is the seat's
   *  equipped cosmetics as a serialized loadout string — the showroom car
   *  wears the shop gear every client will see on track. */
  setSeat(slot: number, vehicleId: string, ready: boolean, look = ''): void {
    const seat = this.seats[slot];
    if (!seat) return;
    seat.filled = !!vehicleId;
    void ready; // readiness renders in the DOM header (M64) — rings stay livery
    this.refreshRings();
    if (seat.vehicleId === vehicleId && seat.lookKey === look) return;
    seat.vehicleId = vehicleId;
    seat.lookKey = look;
    const token = ++seat.loadToken;
    seat.carHolder.clear();
    if (!vehicleId) return;
    const def = VEHICLES[vehicleId] ?? VEHICLES.sports;
    // M93: the showroom car wears its VEHICLE stock livery (garage parity);
    // the seat RING keeps the slot colour — that ring is your ball colour, so
    // the lobby is where you learn which ball is yours before the grid.
    const color = stockColorOf(vehicleId);
    modelLoader
      .load(def.url)
      .then((g) => {
        if (token !== seat.loadToken) return;
        const fitted = ModelLoader.fit(g, def.length, def.forwardIsZ);
        const resolved = resolveLook(parseLoadoutString(look));
        applyCarMaterials(fitted, color, resolved, vehicleId);
        seat.carHolder.clear();
        seat.carHolder.add(fitted);
        attachModRig(seat.carHolder, resolved, vehicleId, color, surfaceFromObject(fitted, seat.carHolder)); // M128: the seat wears its mods

      })
      .catch(() => {
        if (token !== seat.loadToken) return;
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(1.9, 0.9, 4),
          new THREE.MeshStandardMaterial({ color, roughness: 0.35, metalness: 0.5 }),
        );
        body.position.y = 0.75;
        seat.carHolder.clear();
        seat.carHolder.add(body);
      });
  }

  private refreshRings(): void {
    this.seats.forEach((seat, i) => {
      const mine = i === this.localSlot;
      seat.ringMat.color.set(RACER_POOL[i]?.color ?? 0x33415e);
      seat.ringMat.opacity = seat.filled ? (mine ? 1.0 : 0.75) : 0.14;
      seat.shadowMat.opacity = seat.filled ? 1 : 0.25;
    });
  }

  show(): void {
    this.ensureRenderer();
    if (this.renderer) this.renderer.domElement.style.display = '';
    this.resize();
    if (this.raf === null) {
      this.lastT = performance.now();
      const tick = (): void => {
        this.raf = requestAnimationFrame(tick);
        const now = performance.now();
        const dt = Math.min(0.1, (now - this.lastT) / 1000);
        this.lastT = now;
        for (const s of this.seats) {
          if (!this.reducedMotion) {
            s.phase += dt * ROT_SPEED;
            s.carHolder.rotation.y = s.phase;
          }
          // Empty seats breathe softly — the grid feels alive while it fills.
          if (!s.filled && !this.reducedMotion) {
            const pulse = 0.1 + 0.06 * Math.sin(now / 480 + s.phase * 3);
            s.ringMat.opacity = pulse;
          }
        }
        this.renderer?.render(this.scene, this.camera);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }

  hide(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    if (this.renderer) this.renderer.domElement.style.display = 'none';
  }

  resize(): void {
    if (!this.renderer) return;
    const w = Math.max(1, this.host.clientWidth);
    const h = Math.max(1, this.host.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.hide();
    this.env?.dispose();
    this.shadowTex?.dispose();
    this.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) for (const m of mat) m.dispose();
      else if (mat) mat.dispose();
    });
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
    this.renderer = null;
  }
}
